/* eslint-disable @typescript-eslint/no-explicit-any */
import * as yup from 'yup';
import { FORBIDDEN_LANG_REGEX, FORBIDDEN_SPECIAL_CHAR_REGEX } from './regex';
import { TFunction } from 'i18next';
import { downloadFile, getMessageText } from './helpers';
import { ALERT_TYPE, TKeys } from './enums';
import { FIELD_LENGTH, FORMAT_DATETIME } from './constants';
import { formatDate } from './date';
import moment from 'moment';

/**
 * ✅ Reusable validation function for some fields that only allow alphabets and numbers
 * @param label - Field name to display in error messages
 * @param maxLength - Length of the field (default: FIELD_LENGTH.max = 40)
 */
export const validateOnlyAlphabet = (t: TFunction, label: string, maxLength = FIELD_LENGTH.max) => {
    const reqMsg = getMessageText(t(TKeys.MSE00001), label);
    const langMsg = getMessageText(t(TKeys.MSE00034), label);

    return yup
        .string()
        .required(reqMsg)
        .test(
            'no-special-chars',
            langMsg,
            (value) => !FORBIDDEN_SPECIAL_CHAR_REGEX.test(value || ''),
        )
        .test(
            'no-japanese-or-vietnamese',
            langMsg,
            (value) => !FORBIDDEN_LANG_REGEX.test(value || ''),
        )
        .max(maxLength, getMessageText(t(TKeys.MSE00004), label));
};

/**
 * ✅ Reusable validation function for some fields like company name
 * @param label - Field name to display in error messages
 * @param maxLength - Length of the field (default: FIELD_LENGTH.max = 40)
 */
export const validateRequiredWithMaxLength = (
    t: TFunction,
    label: string,
    maxLength = FIELD_LENGTH.max,
) => {
    const lengthMsg = t(TKeys.MSE00004);
    const lengthMsgWithLabel = getMessageText(lengthMsg, label, maxLength);
    const reqMsg = getMessageText(t(TKeys.MSE00001), label);

    return yup.string().max(maxLength, lengthMsgWithLabel).required(reqMsg);
};

export const validateComment = (t: TFunction, label = t(TKeys.LBL120084)) => {
    const lengthMsg = t(TKeys.MSE00004);
    const lengthMsgWithLabel = getMessageText(lengthMsg, label, FIELD_LENGTH.comment);
    return yup.string().nullable().max(FIELD_LENGTH.comment, lengthMsgWithLabel);
};

export const validateRequiredEnter = (t: TFunction, label: string) => {
    const msg = t(TKeys.MSE00001);
    const reqMsg = getMessageText(msg, label);
    return yup.string().required(reqMsg);
};

export const validateRequiredSelect = (t: TFunction, label: string) => {
    const reqMsg = t(TKeys.MSE00014);
    const reqMsgWithLabel = getMessageText(reqMsg, label);
    return yup.string().required(reqMsgWithLabel);
};

export const validateNumber = (
    t: TFunction,
    label: string,
    maxLength = FIELD_LENGTH.numberContainer,
) => {
    const invalidMsg = t(TKeys.MSE00016);
    const invalidTypeWithLabel = getMessageText(invalidMsg, label);

    const maxLengthMsg = t(TKeys.MSE00003);
    const maxLengthMsgWithLabel = getMessageText(maxLengthMsg, label, maxLength);

    return yup
        .number()
        .transform((value, originalValue) => {
            if (originalValue === '') return null;
            return isNaN(value) ? NaN : value;
        })
        .nullable()
        .typeError(invalidTypeWithLabel)
        .max(maxLength, maxLengthMsgWithLabel);
};

/**
 * Downloads a file CSV.
 * @param fetcher - A function that receives URLSearchParams and returns a Promise<ApiResponse<any>>
 * @param filter - The filter object used to build the request parameters
 * @param fileName - The name to be used for the downloaded file
 * @param handleShowAlert - A callback function to display an alert when an error occurs
 */
export const handleDownload = async ({
    fetcher,
    filter,
    fileName,
    handleShowAlert,
}: {
    fetcher: (params: URLSearchParams) => Promise<ApiResponse<any>>;
    filter: Record<string, any>;
    fileName: string;
    handleShowAlert: (message: string, type: ALERT_TYPE) => void;
}) => {
    const cleanedFilter = Object.fromEntries(
        Object.entries(filter || {})
            .filter(([, value]) => value !== '' && value !== undefined)
            .map(([key, value]) => [
                key,
                key.toLowerCase().includes('update_at')
                    ? formatDate(`${value}`, FORMAT_DATETIME.YYYYMMDD2)
                    : value,
            ]),
    );
    const params = new URLSearchParams({
        filter: JSON.stringify(cleanedFilter),
    });

    const response = await fetcher(params);
    const now = moment().format('YYYYMMDDHHmmss');

    if (response.success) {
        downloadFile({
            byteArray: response.data,
            fileName: `${fileName}_${now}`,
            isCsv: true,
        });
    } else {
        handleShowAlert(response.error, ALERT_TYPE.ERROR);
    }
};

export type CsvErrorCode = 'EMPTY_FILE' | 'NO_DATA_ROW' | 'READ_ERROR';

export class CsvError extends Error {
    code: CsvErrorCode;

    constructor(code: CsvErrorCode) {
        super(code);
        this.code = code;
    }
}
